/* eslint-disable import/prefer-default-export */
import cloneDeep from 'lodash.clonedeep';
import {
  FormTemplate,
  FormTemplateSection,
  FormTemplateOption,
  FormTemplateField,
} from 'data/template/template.types';
import { RegisteredNode } from 'data/registeredNode/registeredNode.types';
import { NodeData } from 'data/nodes/nodes.types';

export type ListenersIpAddressPortsType = {
  [key:string]: string[],
}

export function adaptTemplateVisibilityMode(
  template: FormTemplate | undefined,
  mode: 'create' | 'edit' | 'view',
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const clonedTemplate = cloneDeep(template);

  if (mode === 'edit') {
    (clonedTemplate.main as FormTemplateSection).fields.nodeName.readOnly = true;
    (clonedTemplate.main as FormTemplateSection).fields.siteName.readOnly = true;
    (clonedTemplate.main as FormTemplateSection).fields.swVersion.readOnly = true;
  } else if (mode === 'view') {
    setAllFieldsAsReadOnlyExcludingFields(clonedTemplate, []);
  }

  return clonedTemplate;
}

export function adaptTemplateOptions(
  template: FormTemplate | undefined,
  countries: string[],
  registeredNode?: RegisteredNode,
  xcipioNodes?: NodeData[]
): FormTemplate | undefined {
  if (template == null) {
    return template;
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  if (xcipioNodes) {
    setOptionForTemplateSection(
      clonedTemplate.main as FormTemplateSection,
      getXcipioNodes(xcipioNodes),
      'nodeName'
    );
  }

  if (registeredNode && registeredNode.nodeName) {
    (clonedTemplate.main as FormTemplateSection).fields.nodeName.initial = registeredNode.nodeName;
    (clonedTemplate.main as FormTemplateSection).fields.nodeType.initial = registeredNode.nodeType;
    (clonedTemplate.main as FormTemplateSection).fields.topology.initial = registeredNode.topology;
    (clonedTemplate.main as FormTemplateSection).fields.ipAddr.initial = registeredNode.ipAddr;
    (clonedTemplate.main as FormTemplateSection).fields.ipAddr6.initial = registeredNode.ipAddr6;
    (clonedTemplate.main as FormTemplateSection).fields.status.initial = registeredNode.status;
  }

  const countryOptions = getFieldOptionsForLocation(countries);
  setLocationFieldOptions(clonedTemplate, countryOptions, 'country');

  return clonedTemplate;
}

export function adaptTemplateBasedOnAppSettings(
  template: FormTemplate | undefined,
  isLocationRequired?: boolean,
): FormTemplate | undefined {

  if (template == null) {
    return template;
  }

  const clonedTemplate = cloneDeep(template);

  if (isLocationRequired) {
    adaptLocationFields(clonedTemplate, isLocationRequired);
  }

  return clonedTemplate;
}

function adaptLocationFields(template: FormTemplate, isLocationRequired: boolean): void {
  const mainFields = (template.main as FormTemplateSection).fields;

  if (mainFields?.country?.validation?.required !== undefined) {
    mainFields.country.validation.required = isLocationRequired;
  }

  if (mainFields?.stateName?.validation?.required !== undefined) {
    mainFields.stateName.validation.required = isLocationRequired;
  }

  if (mainFields?.city?.validation?.required !== undefined) {
    mainFields.city.validation.required = isLocationRequired;
  }
}

function getFieldOptionsForLocation(list: string[]): FormTemplateOption[] {
  return list.map((entry) => ({
    label: entry,
    value: entry,
  }));
}

function setLocationFieldOptions(
  template: FormTemplate,
  options: FormTemplateOption[],
  field: 'country' | 'stateName' | 'city',
): void {
  const templateSection = (template.main as FormTemplateSection);

  if (templateSection.fields[field] == null) {
    return;
  }

  const firstOption = templateSection.fields[field].options?.[0];
  if (firstOption) {
    templateSection.fields[field].options = [firstOption];
  }

  if (options.length > 0) {
    templateSection.fields[field].options?.push(...options);
    templateSection.fields[field].disabled = false;
  } else {
    templateSection.fields[field].disabled = true;
  }
}

export function adaptLocationField(
  template: FormTemplate,
  list: string[],
  field: 'stateName' | 'city',
): FormTemplate {

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  const options: FormTemplateOption[] = getFieldOptionsForLocation(list);
  setLocationFieldOptions(clonedTemplate, options, field);

  return clonedTemplate;
}

function setOptionForTemplateSection(
  templateSection: FormTemplateSection,
  options: FormTemplateOption[],
  fieldName: string
) {
  const { fields } = templateSection;
  const templateField = fields[fieldName];
  if (templateField == null || templateField?.options == null || options == null) {
    return;
  }

  setOptionFormTemplateField(templateField, options);
}

function setOptionFormTemplateField(
  templateField: FormTemplateField,
  options: FormTemplateOption[],
) {
  if (options.length > 0) {
    templateField.options?.push(...options);
    templateField.initial = templateField.options?.[0].value;
  } else {
    templateField.initial = '';
  }
}

function getXcipioNodes(
  xcipioNodes: NodeData[]
): FormTemplateOption[] {
  return xcipioNodes?.map((field) => ({
    label: field.nodeName,
    value: field.nodeName,
  }));
}

function setAllFieldsAsReadOnlyExcludingFields(template: FormTemplate, excludingFields: string[]) {
  Object.entries(template).forEach(([_templateKey, templateEntry]) => {
    if (typeof templateEntry === 'string') {
      return;
    }
    Object.entries(templateEntry.fields).forEach(([fieldKey, fieldEntry]) => {
      if (!excludingFields.includes(fieldKey)) {
        fieldEntry.readOnly = true;
      }
    });
  });
}

export function adaptRegisteredNodeTemplate(
  template: FormTemplate,
): FormTemplate {

  const clonedTemplate = cloneDeep(template);

  clonedTemplate.key = Date.now().toString();
  clonedTemplate.formId = Date.now().toString();

  return clonedTemplate;
}

export function adaptFormData(
  data: RegisteredNode,
): RegisteredNode {

  return cloneDeep(data);
}

export function adaptRegisteredNodeFormData(
  registeredNode: RegisteredNode,
): RegisteredNode {

  const clonedRegisteredNode = cloneDeep(registeredNode);

  return clonedRegisteredNode;
}
